import {IntersectionOf} from '~/shared/utils/types';

const toneDayRGB = {
    10: 'rgba(255, 255, 255, 1)',
    20: 'rgba(10, 10, 10, 0.04)',
    30: 'rgba(10, 10, 10, 0.07)',
    40: 'rgba(10, 10, 10, 0.12)',
    50: 'rgba(10, 10, 10, 0.24)',
    60: 'rgba(10, 10, 10, 0.38)',
    70: 'rgba(10, 10, 10, 0.56)',
    80: 'rgba(10, 10, 10, 0.64)',
    90: 'rgba(10, 10, 10, 0.77)',
    100: 'rgba(10, 10, 10, 1)',
};

const toneNightRGB = {
    10: 'rgba(10, 10, 10, 1)',
    20: 'rgba(255, 255, 255, 0.13)',
    30: 'rgba(255, 255, 255, 0.18)',
    40: 'rgba(255, 255, 255, 0.22)',
    50: 'rgba(255, 255, 255, 0.32)',
    60: 'rgba(255, 255, 255, 0.43)',
    70: 'rgba(255, 255, 255, 0.54)',
    80: 'rgba(255, 255, 255, 0.68)',
    90: 'rgba(255, 255, 255, 0.87)',
    100: 'rgba(255, 255, 255, 1)',
};

const extraColorsRaw = {
    info: {
        10: 'rgba(11, 92, 215, 0.08)',
        20: 'rgba(11, 92, 215, 0.3)',
        30: '#0B5CD7',
        40: '#083B87',
    },
    gold: {
        10: 'rgba(255, 186, 41, 0.2)',
        20: 'rgba(255, 186, 41, 0.44)',
        30: '#FFBA29',
        40: '#AF7F00',
    },
    cyan: {
        10: 'rgba(55, 196, 182, 0.1)',
        20: '#37C4B6',
    },
    pink: {
        10: 'rgba(200, 64, 200, 0.1)',
        20: '#C840C8',
    },
    blue: {
        10: 'rgba(64, 132, 200, 0.1)',
        20: '#4084C8',
    },
    plum: {
        10: 'rgba(149, 56, 255, 0.1)',
        20: '#9538FF',
    },
};

const mainColorsRaw = {
    neutral: {
        10: '#FFFFFF',
        20: '#F5F5F5',
        30: '#EDEDED',
        40: '#E0E0E0',
        50: '#C2C2C2',
        60: '#9E9E9E',
        70: '#757575',
        80: '#616161',
        90: '#404040',
        100: '#0A0A0A',
    },
    shade: toneDayRGB,
    glare: toneNightRGB,

    primary: {
        10: 'rgba(21, 176, 219, 0.1)',
        20: 'rgba(21, 176, 219, 0.33)',
        30: '#15B0DB',
        40: '#0E7592',
    },

    danger: {
        10: 'rgba(224, 0, 0, 0.08)',
        20: 'rgba(224, 0, 0, 0.16)',
        30: '#E00000',
        40: '#630000',
    },

    warning: {
        10: 'rgba(234, 120, 14, 0.09)',
        20: 'rgba(234, 120, 14, 0.28)',
        30: '#EA780E',
        40: '#653508',
    },

    success: {
        10: 'rgba(0, 130, 67, 0.08)',
        20: 'rgba(0, 130, 67, 0.29)',
        30: '#008243',
        40: '#005029',
    },
};

export const colorRaw = {
    ...mainColorsRaw,
    ...extraColorsRaw,
};

const {fromEntries, entries, keys} = Object;

type Color = {colorName: string; colorGrade: string | number};

const formatVar = (parts: Color) => `--${parts.colorName}-${parts.colorGrade}`;
const formatVarUsage = (parts: Color) => `var(${formatVar(parts)})`;

/**
 * Generates something like this:
 * @example
 *  ({
 *  shade: {
 *      10: "var(--shade-10)";
 *      // ...
 *  },
 *  primary: {
 *      10: "var(--primary-10)";
 *      // ...
 *  }})
 */
const generateColorVariables = () => {
    return fromEntries(
        entries(colorRaw).map(([colorName, colorGrades]) => [
            colorName,
            fromEntries(keys(colorGrades).map(grade => [grade, formatVarUsage({colorName, colorGrade: grade})])),
        ])
    ) satisfies {
        [key: string]: {
            [key: string]: string;
        };
    } as {
        [I in keyof typeof colorRaw]: {
            [J in keyof (typeof colorRaw)[I]]: `var(--${I}-${Extract<J, number>})`;
        };
    };
};

/**
 * Converts rgb color scheme to css variables definition;
 * @example
 *  ({
 *  '--shade-10': 'rgba(...)',
 *  '--primary-10': 'rgba(...)',
 *  }})
 */
export const generateVariableDefinitions = () => {
    return fromEntries(
        entries(colorRaw).flatMap(([colorName, colorGrades]) =>
            entries(colorGrades).map(([grade, color]) => [formatVar({colorName, colorGrade: grade}), color])
        )
    ) satisfies {
        [key: string]: string;
    };
};

/**
 * Squashes color name and grade together to make flat object with keys like `primary10`, `primary20`, `primary40`, etc.
 */
const generateFlatColorScheme = (colors: ReturnType<typeof generateColorVariables>) => {
    type FlatColorScheme<T extends Record<string, Record<number, string>>> = IntersectionOf<
        {
            [I in keyof T]: {
                [J in keyof T[I] as `${Extract<I, string>}${Extract<J, number>}`]: T[I][J];
            };
        }[keyof T]
    >;
    return fromEntries(
        entries(colors).flatMap(([colorName, colorGrades]) =>
            entries(colorGrades).map(([grade, color]) => [`${colorName}${grade}`, color])
        )
    ) satisfies {
        [key: string]: string;
    } as FlatColorScheme<typeof colorRaw>;
};

export const colorVar = generateColorVariables();
export const color = generateFlatColorScheme(colorVar);
